<template>
  <v-card variant="outlined" class="pa-2">
    <v-card-title class="text-subtitle-1 mb-1 pa-2">
      <v-icon class="me-1" size="small">mdi-briefcase-outline</v-icon>
      Previous Employment
      <v-spacer></v-spacer>
      <v-chip v-if="loading" color="info" size="small">
        <v-icon class="me-1" size="x-small">mdi-loading</v-icon>
        Loading...
      </v-chip>
    </v-card-title>
    

    <!-- Employment Timeline -->
    <v-card v-if="dataLoaded && previousEmployments.length > 0" variant="outlined" class="ma-2">
      <v-card-title class="text-subtitle-2 pa-2">
        <v-icon class="me-1" size="small">mdi-timeline-clock-outline</v-icon>
        Employment History Timeline
      </v-card-title>
      <v-card-text class="pa-2">
        <div class="timeline-container">
          <div 
            v-for="(employment, index) in previousEmployments" 
            :key="employment.id || index"
            class="timeline-item"
            :class="{ 'timeline-item-last': index === previousEmployments.length - 1 }"
          >
            <!-- Timeline Line -->
            <div class="timeline-line" v-if="index < previousEmployments.length - 1"></div>
            
            <!-- Timeline Dot -->
            <div class="timeline-dot">
              <span class="timeline-number">{{ index + 1 }}</span>
            </div>
            
            <!-- Timeline Content -->
            <div class="timeline-content">
              <v-card variant="outlined" class="employment-card">
                <v-card-text class="pa-3">
                  <!-- Header Row -->
                  <div class="d-flex justify-space-between align-center mb-2">
                    <div>
                      <h4 class="text-h6 text-primary mb-1">{{ employment.company_name || 'Company Name' }}</h4>
                      <p class="text-subtitle-2 text-medium-emphasis mb-0">
                        {{ getDesignationText(employment.designation) || 'Designation' }}
                      </p>
                    </div>
                    <div class="text-right">
                      <v-chip 
                        :color="getJobTypeColor(employment.job_type)" 
                        size="small" 
                        variant="tonal"
                      >
                        {{ getJobTypeText(employment.job_type) || 'Job Type' }}
                      </v-chip>
                    </div>
                  </div>
                  
                  <!-- Duration Row -->
                  <div class="d-flex align-center mb-2">
                    <v-icon size="small" class="me-2 text-medium-emphasis">mdi-calendar-range</v-icon>
                    <span class="text-body-2 text-medium-emphasis">
                      {{ formatDate(employment.date_of_joining) || 'Start Date' }} - 
                      {{ formatDate(employment.date_of_relieving) || 'End Date' }}
                    </span>
                    <v-spacer></v-spacer>
                    <span class="text-body-2 text-medium-emphasis">
                      {{ calculateDuration(employment.date_of_joining, employment.date_of_relieving) }}
                    </span>
                  </div>
                  
                  <!-- Salary Row -->
                  <div v-if="employment.last_drawn_salary" class="d-flex align-center mb-2">
                    <v-icon size="small" class="me-2 text-medium-emphasis">mdi-currency-bdt</v-icon>
                    <span class="text-body-2 text-medium-emphasis">
                      Last Salary: ৳{{ employment.last_drawn_salary.toLocaleString() }}
                    </span>
                  </div>
                  
                  <!-- Address Row -->
                  <div v-if="employment.company_address" class="d-flex align-center mb-2">
                    <v-icon size="small" class="me-2 text-medium-emphasis">mdi-map-marker</v-icon>
                    <span class="text-body-2 text-medium-emphasis">{{ employment.company_address }}</span>
                  </div>
                  
                  <!-- Reporting To Row -->
                  <div v-if="employment.reporting_to" class="d-flex align-center mb-2">
                    <v-icon size="small" class="me-2 text-medium-emphasis">mdi-account-tie</v-icon>
                    <span class="text-body-2 text-medium-emphasis">
                      Reporting to: {{ employment.reporting_to }}
                      <span v-if="employment.reporting_officer_contact" class="ml-2">
                        ({{ employment.reporting_officer_contact }})
                      </span>
                    </span>
                  </div>
                  
                  <!-- Reason for Leaving Row -->
                  <div v-if="employment.reason_for_leaving" class="d-flex align-start mb-2">
                    <v-icon size="small" class="me-2 text-medium-emphasis mt-1">mdi-comment-text</v-icon>
                    <span class="text-body-2 text-medium-emphasis">{{ employment.reason_for_leaving }}</span>
                  </div>
                  
                  <!-- Action Buttons -->
                  <div class="d-flex justify-end mt-3">
                    <v-btn
                      size="small"
                      variant="outlined"
                      color="primary"
                      @click="editEmployment(index)"
                      class="me-2"
                    >
                      <v-icon size="small" class="me-1">mdi-pencil</v-icon>
                      Edit
                    </v-btn>
                    <v-btn
                      size="small"
                      variant="outlined"
                      color="error"
                      @click="removeEmployment(index)"
                    >
                      <v-icon size="small" class="me-1">mdi-delete</v-icon>
                      Remove
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- No Data Message -->
    <v-alert v-else-if="dataLoaded && previousEmployments.length === 0" type="info" variant="tonal" class="ma-2">
      <v-icon class="me-2">mdi-information</v-icon>
      No previous employment records found for this employee.
    </v-alert>
    
    <!-- Form Section Title -->
    <v-card v-if="previousEmployments.length > 0" variant="outlined" class="ma-2">
      <v-card-title class="text-subtitle-2 pa-2">
        <v-icon class="me-1" size="small">mdi-form-select</v-icon>
        Edit Previous Employment Records
      </v-card-title>
    </v-card>

    <!-- Employment Records List -->
    <div v-for="(employment, index) in previousEmployments" :key="index" class="mb-2" :data-employment-index="index">
      <v-card variant="outlined" class="pa-2">
        <v-form :ref="`employmentForm${index}`" class="pa-1">
          <v-row class="ma-0">
            <!-- Left Column -->
            <v-col cols="12" md="6" class="pa-1">
              <v-text-field
                v-model="employment.company_name"
                label="Company Name"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :rules="[v => !!v || 'Company Name is required']"
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-date-input
                v-model="employment.date_of_joining"
                prepend-icon=""
                append-inner-icon="mdi-calendar"
                label="Date of Joining"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :rules="[v => !!v || 'Date of Joining is required']"
                required
              ></v-date-input>
            </v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-date-input
                v-model="employment.date_of_relieving"
                prepend-icon=""
                append-inner-icon="mdi-calendar"
                label="Date of Relieving"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :rules="[v => !!v || 'Date of Relieving is required']"
                required
              ></v-date-input>
            </v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-select
                v-model="employment.designation"
                label="Designation"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :items="designationOptions"
                item-title="text"
                item-value="value"
                placeholder="Please select"
                :rules="[v => !!v || 'Designation is required']"
                required
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-text-field
                v-model="employment.last_drawn_salary"
                label="Last Drawn Salary"
                variant="outlined"
                density="compact"
                hide-details="auto"
                type="number"
                prefix="৳"
              ></v-text-field>
            </v-col>
            
            <!-- Right Column -->
            <v-col cols="12" md="6" class="pa-1">
              <v-textarea
                v-model="employment.company_address"
                label="Company Address"
                variant="outlined"
                density="compact"
                hide-details="auto"
                rows="3"
                auto-grow
              ></v-textarea>
            </v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-select
                v-model="employment.job_type"
                label="Job Type"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :items="jobTypeOptions"
                item-title="text"
                item-value="value"
                placeholder="Select"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-textarea
                v-model="employment.reason_for_leaving"
                label="Reason For Leaving"
                variant="outlined"
                density="compact"
                hide-details="auto"
                rows="3"
                auto-grow
              ></v-textarea>
            </v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-text-field
                v-model="employment.reporting_to"
                label="Reporting To"
                variant="outlined"
                density="compact"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-text-field
                v-model="employment.reporting_officer_contact"
                label="Reporting Officer Contact No."
                variant="outlined"
                density="compact"
                hide-details="auto"
                type="tel"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
        
        <!-- Action Buttons -->
        <v-row class="ma-0 mt-1">
          <v-col cols="12" class="pa-1 d-flex justify-end">
            <v-btn
              color="error"
              variant="outlined"
              size="small"
              @click="removeEmployment(index)"
              :disabled="previousEmployments.length === 1"
            >
              <v-icon class="me-1" size="small">mdi-delete</v-icon>
              Remove
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </div>
    
    <!-- Add More Button -->
    <v-row class="ma-0">
      <v-col cols="12" class="pa-1">
        <v-btn
          color="primary"
          variant="outlined"
          size="small"
          @click="addEmployment"
          block
        >
          <v-icon class="me-1" size="small">mdi-plus</v-icon>
          Add More..
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { toast } from 'vue3-toastify'

// Props
const props = defineProps({
  previousEmployments: {
    type: Array,
    required: true,
    default: () => []
  },
  employeeId: {
    type: [String, Number],
    required: true
  },
  employeeJoiningDate: {
    type: String,
    required: false,
    default: ''
  }
})

// Emits
const emit = defineEmits(['update:previousEmployments'])

// Store
const store = useStore()

// Local refs
const employmentForms = ref([])
const loading = ref(false)
const dataLoaded = ref(false)

// Dropdown options
const designationOptions = [
  { text: 'Software Engineer', value: 'software_engineer' },
  { text: 'Senior Software Engineer', value: 'senior_software_engineer' },
  { text: 'Team Lead', value: 'team_lead' },
  { text: 'Project Manager', value: 'project_manager' },
  { text: 'Business Analyst', value: 'business_analyst' },
  { text: 'UI/UX Designer', value: 'ui_ux_designer' },
  { text: 'DevOps Engineer', value: 'devops_engineer' },
  { text: 'Quality Assurance', value: 'quality_assurance' },
  { text: 'Data Analyst', value: 'data_analyst' },
  { text: 'Marketing Manager', value: 'marketing_manager' },
  { text: 'Sales Executive', value: 'sales_executive' },
  { text: 'HR Manager', value: 'hr_manager' },
  { text: 'Accountant', value: 'accountant' },
  { text: 'Administrative Assistant', value: 'administrative_assistant' },
  { text: 'Operations Manager', value: 'operations_manager' },
  { text: 'Other', value: 'other' }
]

const jobTypeOptions = [
  { text: 'Full Time', value: 'full_time' },
  { text: 'Part Time', value: 'part_time' },
  { text: 'Contract', value: 'contract' },
  { text: 'Internship', value: 'internship' },
  { text: 'Freelance', value: 'freelance' },
  { text: 'Consultant', value: 'consultant' }
]

// Local employment records (not bound to props)
const previousEmployments = ref([])

// Methods
const addEmployment = () => {
  const newEmployment = {
    company_name: '',
    date_of_joining: props.employeeJoiningDate || '',
    date_of_relieving: '',
    designation: '',
    last_drawn_salary: '',
    company_address: '',
    job_type: '',
    reason_for_leaving: '',
    reporting_to: '',
    reporting_officer_contact: ''
  }
  
  previousEmployments.value.push(newEmployment)
}

const removeEmployment = (index) => {
  if (previousEmployments.value.length > 1) {
    previousEmployments.value.splice(index, 1)
  }
}

// Helper functions for table display
const getDesignationText = (value) => {
  const option = designationOptions.find(opt => opt.value === value)
  return option ? option.text : value
}

const getJobTypeText = (value) => {
  const option = jobTypeOptions.find(opt => opt.value === value)
  return option ? option.text : value
}

const getJobTypeColor = (value) => {
  const colorMap = {
    'full_time': 'success',
    'part_time': 'info',
    'contract': 'warning',
    'internship': 'primary',
    'freelance': 'secondary',
    'consultant': 'purple'
  }
  return colorMap[value] || 'grey'
}

const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return ''
  
  try {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 30) {
      return `${diffDays} days`
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30)
      return `${months} month${months > 1 ? 's' : ''}`
    } else {
      const years = Math.floor(diffDays / 365)
      const remainingMonths = Math.floor((diffDays % 365) / 30)
      let result = `${years} year${years > 1 ? 's' : ''}`
      if (remainingMonths > 0) {
        result += ` ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`
      }
      return result
    }
  } catch (error) {
    return ''
  }
}

const formatDate = (dateString) => {
  if (!dateString) return null
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB') // DD/MM/YYYY format
  } catch (error) {
    return dateString
  }
}

const editEmployment = (index) => {
  // Scroll to the form section for this employment record
  const formElement = document.querySelector(`[data-employment-index="${index}"]`)
  if (formElement) {
    formElement.scrollIntoView({ behavior: 'smooth' })
  }
}


const validateAll = async () => {
  const results = []
  for (let i = 0; i < employmentForms.value.length; i++) {
    if (employmentForms.value[i]) {
      const result = await employmentForms.value[i].validate()
      results.push(result)
    }
  }
  return results.every(result => result?.valid !== false)
}

// Watch for changes in local data and emit updates to parent
watch(previousEmployments, (newValue) => {
  emit('update:previousEmployments', newValue)
}, { deep: true })

// Load previous employment data
const loadPreviousEmploymentInfo = async () => {
  if (!props.employeeId) {
    return
  }
  
  loading.value = true
  dataLoaded.value = false
  
  try {
    await store.dispatch('previousEmploymentInfo/fetchPreviousEmploymentInfoByEmployee', props.employeeId)
    const employmentData = store.getters['previousEmploymentInfo/getPreviousEmploymentInfoList']
    
    if (employmentData && employmentData.length > 0) {
      // Map backend data to frontend format
      const mappedData = employmentData.map(emp => ({
        id: emp.id,
        company_name: emp.company_name || '',
        date_of_joining:  new Date(emp.date_of_joining) || '',
        date_of_relieving: new Date(emp.date_of_relieving) || '',
        designation: emp.designation || '',
        last_drawn_salary: emp.last_drawn_salary || '',
        company_address: emp.company_address || '',
        job_type: emp.job_type || '',
        reason_for_leaving: emp.reason_for_leaving || '',
        reporting_to: emp.reporting_to || '',
        reporting_officer_contact: emp.reporting_officer_contact || ''
      }))

      console.log(mappedData)
      
      previousEmployments.value = mappedData
      dataLoaded.value = true
    } else {
      // If no data, ensure at least one empty record
      if (previousEmployments.value.length === 0) {
        addEmployment()
      }
      dataLoaded.value = true
    }
  } catch (error) {
  
    // Still add an empty record if there's an error
    if (previousEmployments.value.length === 0) {
      addEmployment()
    }
  } finally {
    loading.value = false
  }
}

// Save all previous employment information
const saveAllPreviousEmploymentInfo = async () => {
  if (!props.employeeId) {
    throw new Error('Employee ID is required')
  }
  
  try {
    // Filter out empty records
    const validEmploymentData = previousEmployments.value.filter(emp => 
      emp.company_name || emp.designation || emp.date_of_joining
    )
    
    await store.dispatch('previousEmploymentInfo/saveEmployeePreviousEmploymentInfo', {
      employeeId: props.employeeId,
      employmentData: validEmploymentData
    })
    
    toast.success('Previous employment information saved successfully')
  } catch (error) {
   const backendErrors = error.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
          Object.values(backendErrors)
              .flat()
              .forEach(msg => toast.error(msg));
      } else {
          const msg = error.response?.data?.message || 'Failed to save personal information';
          toast.error(msg);
      }
      return false;      
  }
}

// Watch for employee ID changes
watch(() => props.employeeId, (newEmployeeId) => {
  if (newEmployeeId) {
    loadPreviousEmploymentInfo()
  }
}, { immediate: true })

// Load data on component mount
onMounted(() => {
  if (props.employeeId) {
    loadPreviousEmploymentInfo()
  }
})

// Expose form validation methods
defineExpose({
  validate: validateAll,
  addEmployment,
  removeEmployment,
  saveAllPreviousEmploymentInfo
})
</script>

<style scoped>
/* Timeline Styles */
.timeline-container {
  position: relative;
  padding-left: 30px;
}

.timeline-item {
  position: relative;
  margin-bottom: 30px;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -25px;
  top: 20px;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(25, 118, 210, 0.3);
  z-index: 2;
  border: 3px solid white;
}

.timeline-number {
  color: white;
  font-weight: bold;
  font-size: 14px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.timeline-line {
  position: absolute;
  left: -7px;
  top: 60px;
  width: 2px;
  height: calc(100% + 10px);
  background: linear-gradient(to bottom, #e0e0e0, #bdbdbd);
  z-index: 1;
}

.timeline-content {
  margin-left: 20px;
}

.employment-card {
  transition: all 0.3s ease;
  border-left: 4px solid #1976d2;
}

.employment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-left-color: #42a5f5;
}

/* Responsive Design */
@media (max-width: 768px) {
  .timeline-container {
    padding-left: 20px;
  }
  
  .timeline-dot {
    left: -15px;
    width: 30px;
    height: 30px;
  }
  
  .timeline-content {
    margin-left: 10px;
  }
  
  .employment-card .v-card-text {
    padding: 16px !important;
  }
}

/* Animation for timeline items */
.timeline-item {
  animation: slideInRight 0.5s ease-out;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Stagger animation for multiple items */
.timeline-item:nth-child(1) { animation-delay: 0.1s; }
.timeline-item:nth-child(2) { animation-delay: 0.2s; }
.timeline-item:nth-child(3) { animation-delay: 0.3s; }
.timeline-item:nth-child(4) { animation-delay: 0.4s; }
.timeline-item:nth-child(5) { animation-delay: 0.5s; }
</style>
